import type { ReactElement } from "react";
import type { Topic, TopicSection } from "../../data/study/topics/types";
import { topicHeadingId } from "../../data/study/grammatica-index";
import { InlineText } from "./InlineText";

const calloutStyles: Record<"tip" | "warning" | "rule", string> = {
  tip: "border-[var(--accent)]/40 bg-[var(--accent-soft)]",
  warning: "border-[var(--warn)]/40 bg-[var(--warn)]/10",
  rule: "border-[var(--ink-faint)] bg-[var(--panel-soft)]",
};

const svgProps = {
  width: 16,
  height: 16,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  "aria-hidden": true as const,
};

/** Inline SVG glyphs replacing the old emoji-as-icon (brand §7 anti-pattern). */
const CalloutGlyph: Record<"tip" | "warning" | "rule", () => ReactElement> = {
  // lightbulb
  tip: () => (
    <svg {...svgProps}>
      <path d="M9 18h6M10 22h4M12 2a7 7 0 00-4 12.7c.6.5 1 1.3 1 2.3h6c0-1 .4-1.8 1-2.3A7 7 0 0012 2z" />
    </svg>
  ),
  // triangle-warning
  warning: () => (
    <svg {...svgProps}>
      <path d="M12 3l9 16H3zM12 10v4M12 17h.01" />
    </svg>
  ),
  // set-square
  rule: () => (
    <svg {...svgProps}>
      <path d="M3 7l4-4 14 14-4 4zM7 7l3 3M11 11l3 3" />
    </svg>
  ),
};

/** 2×2-grid glyph for the Formule eyebrow. */
function FormulaGlyph() {
  return (
    <svg {...svgProps} width={14} height={14}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function Section({ section }: { section: TopicSection }) {
  switch (section.kind) {
    case "heading":
      return section.level === 2 ? (
        <h2
          id={topicHeadingId(section.text)}
          className="scroll-mt-24 text-2xl sm:text-3xl font-semibold tracking-tight mt-10 mb-3"
        >
          <InlineText text={section.text} />
        </h2>
      ) : (
        <h3
          id={topicHeadingId(section.text)}
          className="scroll-mt-24 text-base font-semibold uppercase tracking-wider text-[var(--ink-muted)] font-mono-badge mt-7 mb-2"
        >
          <InlineText text={section.text} />
        </h3>
      );
    case "paragraph":
      return (
        <p className="leading-relaxed text-[var(--ink)] text-base sm:text-lg max-w-prose">
          <InlineText text={section.text} />
        </p>
      );
    case "list":
      return (
        <ul className="list-disc pl-5 space-y-1.5 text-[var(--ink)] max-w-prose">
          {section.items.map((it, i) => (
            <li key={i}>
              <InlineText text={it} />
            </li>
          ))}
        </ul>
      );
    case "examples":
      return (
        <div className="space-y-3 max-w-prose">
          {section.rows.map((r, i) => (
            <div
              key={i}
              className="rounded-lg border border-[var(--border)] bg-[var(--panel-soft)] px-4 py-3"
            >
              <p className="font-serif-italic text-lg leading-relaxed">{r.dutch}</p>
              <p className="text-sm text-[var(--ink-muted)] mt-1">
                <InlineText text={r.english} />
              </p>
            </div>
          ))}
        </div>
      );
    case "table":
      return (
        <div className="overflow-x-auto rounded-lg border border-[var(--border)] bg-[var(--panel)]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border)]">
                {section.headers.map((h, i) => (
                  <th
                    key={i}
                    className="px-4 py-2.5 text-left font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]"
                  >
                    <InlineText text={h} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.rows.map((row, i) => (
                <tr
                  key={i}
                  className={i < section.rows.length - 1 ? "border-b border-[var(--border)]" : ""}
                >
                  {row.map((cell, j) => (
                    <td
                      key={j}
                      className={`px-4 py-2.5 align-top ${
                        j === 0 && section.emphasizeFirstCol
                          ? "font-medium text-[var(--accent)]"
                          : "text-[var(--ink)]"
                      }`}
                    >
                      <InlineText text={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "callout":
      return (
        <aside
          className={`rounded-xl border-l-4 px-5 py-4 ${calloutStyles[section.tone]} max-w-prose`}
        >
          {section.title ? (
            <p className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)] mb-1 flex items-center gap-1.5">
              {CalloutGlyph[section.tone]()}
              <InlineText text={section.title} />
            </p>
          ) : null}
          <p className="leading-relaxed text-[var(--ink)]">
            <InlineText text={section.text} />
          </p>
        </aside>
      );
    case "rule":
      return (
        <p className="font-serif-italic text-lg text-[var(--ink-muted)] border-l-2 border-[var(--accent)] pl-4 max-w-prose">
          <InlineText text={section.text} />
        </p>
      );
    case "formula":
      return <Formula section={section} />;
  }
}

/**
 * A reusable sentence template. Slots in [brackets] inside `pattern` are
 * highlighted in accent color so the student sees what they swap. Below
 * the pattern, an optional legend explains each slot, then 1-3 worked
 * examples in serif italic.
 */
function Formula({
  section,
}: {
  section: Extract<TopicSection, { kind: "formula" }>;
}) {
  const tokens = section.pattern.split(/(\[[^\]]+\])/g);
  return (
    <div className="rounded-xl border border-[var(--accent)]/30 bg-[var(--panel-soft)] p-5 max-w-prose space-y-3">
      <p className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)] flex items-center gap-1.5">
        <FormulaGlyph />
        <span>
          Formule{" "}
          {section.title ? (
            <span>
              · <InlineText text={section.title} />
            </span>
          ) : null}
        </span>
      </p>
      <p className="font-mono-badge text-base sm:text-lg leading-relaxed">
        {tokens.map((t, i) =>
          t.startsWith("[") && t.endsWith("]") ? (
            <span key={i} className="text-[var(--accent)]">
              {t}
            </span>
          ) : (
            <span key={i} className="text-[var(--ink)]">
              {t}
            </span>
          ),
        )}
      </p>
      {section.legend && section.legend.length > 0 ? (
        <ul className="space-y-1 text-sm">
          {section.legend.map((l, i) => (
            <li key={i} className="flex gap-2">
              <span className="font-mono-badge text-[var(--accent)] shrink-0">
                {l.token}
              </span>
              <span className="text-[var(--ink-muted)]">{l.meaning}</span>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="space-y-2 pt-1 border-t border-[var(--border)]">
        {section.examples.map((e, i) => (
          <div key={i}>
            <p className="font-serif-italic text-base sm:text-lg leading-relaxed text-[var(--ink)]">
              {e.dutch}
            </p>
            {e.english ? (
              <p className="text-xs text-[var(--ink-muted)] mt-0.5">
                <InlineText text={e.english} />
              </p>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

export function TopicRenderer({ topic }: { topic: Topic }) {
  return (
    <article className="space-y-4">
      <header className="space-y-2 mb-2">
        <p className="text-xs uppercase tracking-wider text-[var(--ink-faint)] font-mono-badge">
          A2 · {topic.duration}
        </p>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{topic.title}</h1>
        <p className="font-serif-italic text-lg sm:text-xl text-[var(--ink-muted)] max-w-prose">
          {topic.tagline}
        </p>
      </header>
      {topic.sections.map((s, i) => (
        <Section key={i} section={s} />
      ))}
    </article>
  );
}
